import logo from "../logo/logo.png";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isLocalMode, getStage, isAdminUser } from "../utils/AuthUtils";
import { useAuth } from "./Authentication";
import FeedbackModal from "./FeedbackModal";
import GroupViewSwitcher from "./GroupViewSwitcher";
import TopNavigation, {
  TopNavigationProps,
} from "@amzn/awsui-components-console/top-navigation";
import Badge from "@amzn/awsui-components-console/badge";
import {
  getGroupViewSwitcherEnabled,
  setGroupViewSwitcherEnabled,
} from "../utils/Preferences";

interface TopNavProps {
  isDarkMode: () => boolean;
  enableDarkMode: (enabled: boolean) => void;
}

/**
 * Top navigation
 */
export default function TopNav({ isDarkMode, enableDarkMode }: TopNavProps) {
  const navigate = useNavigate();
  const { user, logout, switchGroupView } = useAuth();

  const [visible, setVisible] = useState(false);
  const [groupViewSwitcherEnabled, setGroupViewSwitcherEnabledState] = useState(
    getGroupViewSwitcherEnabled()
  );

  const getUtilities = () => {
    const result: TopNavigationProps.Utility[] = [];

    if (user) {
      if (!user.partner) {
        result.push({
          type: "button",
          iconName: "status-info",
          title: "Help",
          ariaLabel: "Help",
          disableUtilityCollapse: false,
          onClick: () => {
            navigate("/help");
          },
        });
      }

      result.push({
        type: "menu-dropdown",
        iconName: "settings",
        ariaLabel: "Settings",
        title: "Settings",
        onItemClick: (item) => {
          if (item.detail.id === "mode") {
            enableDarkMode(!isDarkMode());
          } else if (
            item.detail.id === "groupViewSwitcher" &&
            isAdminUser(user)
          ) {
            const newEnabled = !groupViewSwitcherEnabled;
            setGroupViewSwitcherEnabled(newEnabled);
            setGroupViewSwitcherEnabledState(newEnabled);
            if (!newEnabled) {
              switchGroupView("admin");
            }
          }
        },
        items: [
          {
            id: "mode",
            text: isDarkMode() ? "Light mode" : "Dark mode",
          },
          ...(isAdminUser(user?.originalUser || user)
            ? [
                {
                  id: "groupViewSwitcher",
                  text: groupViewSwitcherEnabled
                    ? "Hide Group Switcher"
                    : "Show Group Switcher",
                },
              ]
            : []),
        ],
      });

      let profileName = user ? `${user.firstName} ${user.lastName}` : "";
      if (profileName && getStage() !== "prod") {
        profileName += ` (${getStage()} ${isLocalMode() ? " local" : ""})`;
      }
      result.push({
        type: "menu-dropdown",
        text: profileName,
        description: user ? `${user.email}` : "",
        iconName: "user-profile",
        onItemClick: (event) => {
          if (event.detail.id === "signout") {
            event.preventDefault();
            logout();
          }
        },
        items: [{ id: "signout", text: "Sign out" }],
      });

      result.push({
        type: "button",
        iconName: "envelope",
        ariaLabel: "Feedback and suggestions",
        onClick: () => setVisible(true),
      });
    }

    return result;
  };

  return (
    <>
      <TopNavigation
        identity={{
          href: "/",
          title: "Demo Portal",
          logo: {
            src: logo,
            alt: "Demo Portal",
          },
          onFollow: (event: any) => {
            event.preventDefault();
            navigate(event.detail.href);
          },
        }}
        utilities={getUtilities()}
        search={
          user &&
          isAdminUser(user?.originalUser || user) &&
          groupViewSwitcherEnabled ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <GroupViewSwitcher />
              {user.originalUser && user.currentGroupView !== "admin" && (
                <Badge color="blue">
                  Viewing as{" "}
                  {user.currentGroupView.charAt(0).toUpperCase() +
                    user.currentGroupView.slice(1)}
                </Badge>
              )}
            </div>
          ) : undefined
        }
      />
      <FeedbackModal visible={visible} setVisible={setVisible} />
    </>
  );
}
